<template>
  <vue-carousel-wrap @swipe="swipeInDirection">
    <div class="swiper-base">
      <div class="swiper-base__items swiper-items" :style="itemsStyles">
        <div
          v-for="item of items"
          :key="JSON.stringify(item)"
          ref="item"
          class="swiper-items__item"
        >
          <slot name="item" :item="item"></slot>
        </div>
      </div>

      <slot name="dots"></slot>
    </div>

    <slot name="buttons"></slot>
  </vue-carousel-wrap>
</template>

<script>
import { clamp } from 'ramda';
import { mapState } from 'vuex';
import { VueCarouselWrap } from 'vue-carousel-generic';

import { types } from '@/store/types';

export default {
  name: 'SwiperBase',
  components: { VueCarouselWrap },
  props: {
    items: {
      type: Array,
      required: true
    },
    lastItemOffset: {
      type: Number,
      default: 0
    },
    /* 'none' | 'full' | 'start' | 'end' */
    cycle: {
      type: String,
      default: 'none'
    }
  },
  data() {
    return {
      currentItemIndex: 0,
      itemWidth: 0,
      onWindowResize: null
    };
  },
  computed: {
    ...mapState({
      locale: types.state.LOCALE
    }),
    itemsStyles() {
      return {
        '--current-item-index': this.currentItemIndex
      };
    }
  },
  methods: {
    getNextIndex(index) {
      const min = 0;
      const max = this.items.length - 1 - this.lastItemOffset;

      const cycleBoth = this.cycle === 'full';
      const cycleLeft = cycleBoth || this.cycle === 'start';
      const cycleRight = cycleBoth || this.cycle === 'end';

      if (cycleLeft && index < min) {
        return max;
      }

      if (cycleRight && index > max) {
        return min;
      }

      return clamp(min, max, index);
    },
    getLogicalDirection(swipeEvent) {
      if (['start', 'end'].includes(swipeEvent)) {
        return swipeEvent;
      }

      const logicalDirection = {
        left: this.locale === 'ar' ? 'end' : 'start',
        right: this.locale === 'ar' ? 'start' : 'end'
      };

      return logicalDirection[swipeEvent];
    },
    swipeInDirection(direction) {
      const logicalDirection = this.getLogicalDirection(direction);
      const increment = logicalDirection === 'start' ? 1 : -1;

      this.currentItemIndex = this.getNextIndex(this.currentItemIndex + increment);

      this.$emit('change', this.currentItemIndex);
    },
    swipeToIndex(index) {
      this.currentItemIndex = this.getNextIndex(index);

      this.$emit('change', this.currentItemIndex);
    }
  }
};
</script>

<style lang="scss" scoped>
.swiper-base {
  width: 100%;
  max-width: 100%;
  overflow-x: hidden;
  position: relative;

  &__items {
    height: 100%;
  }
}

.swiper-items {
  display: flex;
  justify-content: flex-start;
  --direction-factor: -1;
  transform: translateX(
    calc(
      var(--direction-factor) * var(--current-item-index) * (var(--item-width) + var(--item-gap))
    )
  );
  transition: transform 1s ease-out;

  &:lang(ar) {
    --direction-factor: 1;
  }

  &__item {
    width: var(--item-width);
    height: var(--item-height, auto);
    flex: 0 0 auto;
  }

  &__item:not(:last-child) {
    margin-inline-end: var(--item-gap);
  }
}
</style>
